import React, { useRef, useState, useContext } from 'react'
import * as colors from '@giphy/js-brand'
import { Content, CTAButton as BrandCTAButton, FormContainer } from 'shared/components/form/form-components'
import styled from 'styled-components'
import { TermsOfServiceLogin, ForgotPassword } from 'shared/components/form/copy'
import { login } from 'shared/api/auth'
import FacebookButton from 'shared/components/form/facebook-button'
import AppleButton from 'shared/components/form/apple-button'
import appendNextUrl from 'shared/components/form/get-next-url'
import LoginFields, { FIELDS } from 'shared/components/form/fields'
import MessageContext from 'shared/contexts/message'
import useCSRFToken from 'shared/components/form/csrftoken-hook'

const sectionMargin = 6

const FacebookButtonWithMargin = styled(FacebookButton)`
    margin-bottom: ${sectionMargin}px;
`

const AppleButtonWithMargin = styled(AppleButton)`
    margin-bottom: ${sectionMargin}px;
`

const FormContent = styled(Content)<{ width: number }>`
    width: ${props => props.width}px;
`

const CTAButton = styled(BrandCTAButton)`
    width: 100%;
    background: linear-gradient(-135deg, #6157ff 0%, #9933ff 100%);
    opacity: ${props => (props.disabled ? '60' : '100')}% !important;
    font-size: 15px;
    border-radius: 3px;
    cursor: pointer;
`

type LoginFormProps = {
    width: number
}

const LoginForm = ({ width }: LoginFormProps) => {
    const [submitEnabled, setSubmitEnabled] = useState<Boolean>(false)
    const { CSRFToken } = useCSRFToken()
    const { sendMessage } = useContext(MessageContext)
    const formRef = useRef<HTMLFormElement | null>(null)

    const onValidChange = (isFormValid: Boolean) => {
        setSubmitEnabled(isFormValid)
    }

    const submit = async event => {
        event.stopPropagation()
        event.preventDefault()

        if (submitEnabled && formRef.current) {
            try {
                await login({
                    email: formRef.current['email'].value,
                    password: formRef.current['password'].value,
                })

                // If there is no catch, we can just reload the page and the backend will
                // redirect to the channels page with the user logged
                location.reload()
            } catch (err) {
                sendMessage(err.details.detail, 'error')
            }
        }
    }

    return (
        <FormContainer>
            <form method="POST" action={appendNextUrl(`/login`)} onSubmit={submit} ref={formRef}>
                <FormContent width={width}>
                    {CSRFToken && <input type="hidden" name="csrfmiddlewaretoken" value={CSRFToken} />}
                    <LoginFields
                        onValidChange={onValidChange}
                        showFields={[FIELDS.email, FIELDS.password]}
                        borderRadius={3}
                    />
                    <CTAButton
                        color={submitEnabled ? colors.primaryCTA : colors.giphyDarkGrey}
                        disabled={!submitEnabled}
                    >
                        Log in
                    </CTAButton>
                    <ForgotPassword />
                    <FacebookButtonWithMargin
                        label="Log In with Facebook"
                        onClick={() => (location.href = `/login/facebook/${location.search}`)}
                    />
                    <AppleButtonWithMargin
                        label="Log In with Apple"
                        onClick={() => (location.href = `/login/apple/${location.search}`)}
                    />
                    <TermsOfServiceLogin />
                </FormContent>
            </form>
        </FormContainer>
    )
}

export default LoginForm
